import { ArrowLeftIcon, MapPinIcon } from '@heroicons/react/24/outline'
import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from 'urql'
import { formatDateRange } from '../../_helpers/formatDateRange'
import { publicFestivalQuery } from '../../_lib/graphql/queries'
import { Spinner } from '../../_ui'
import { FestivalImage } from '../FestivalImage'
import { FestivalLogo } from '../FestivalLogo'
import { PublicLayout } from '../PublicLayout'

export const PublicFestivalPage = () => {
  const { slug } = useParams()
  const [{ data, fetching }] = useQuery({
    query: publicFestivalQuery,
    variables: { slug: slug || '' }
  })

  const festival = data?.publicFestival
  const dateRange = useMemo(() => formatDateRange(festival?.begin, festival?.end), [festival?.begin, festival?.end])

  return (
    <PublicLayout>
      <Link
        to={'/festivals'}
        className={'mb-6 inline-flex items-center gap-2 text-sm font-semibold text-primary-200 hover:underline'}
      >
        <ArrowLeftIcon className={'h-4 w-4'} />
        Alle Festivals
      </Link>

      {fetching && !data && <Spinner />}
      {!fetching && !festival && (
        <p className={'rounded-2xl border border-dashed border-sand-200 bg-white/60 px-5 py-8 text-center text-sm text-gray-600'}>
          Dieses Festival wurde nicht gefunden.
        </p>
      )}

      {festival && (
        <article>
          <FestivalImage
            imageUrl={festival.heroImageUrl}
            title={festival.title}
            className={'h-56 rounded-3xl sm:h-80'}
          >
            <div className={'absolute bottom-0 left-0 right-0 flex items-center gap-4 p-5 sm:p-8'}>
              {festival.logoUrl && (
                <FestivalLogo
                  logoUrl={festival.logoUrl}
                  background={festival.logoBackground}
                  className={'h-16 w-auto max-w-56 shrink-0 rounded-2xl p-3.5'}
                />
              )}
              <h1 className={'text-3xl font-bold tracking-tight text-white text-balance sm:text-5xl'}>
                {festival.title}
              </h1>
            </div>
          </FestivalImage>

          <div className={'mt-8 grid gap-10 lg:grid-cols-3'}>
            <div className={'lg:col-span-2'}>
              {festival.teaser && (
                <p className={'text-xl leading-8 text-primary-200 text-pretty'}>
                  {festival.teaser}
                </p>
              )}
              {festival.description && (
                <div
                  className={'mt-6 max-w-prose text-base leading-7 text-gray-700 [&_a]:text-primary [&_a]:underline [&_h2]:mt-8 [&_h2]:mb-2 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-primary-200 [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:font-bold [&_h3]:text-primary-200 [&_p]:mb-4 [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-6'}
                  dangerouslySetInnerHTML={{ __html: festival.description }}
                />
              )}
            </div>

            <aside className={'h-fit rounded-3xl border border-sand-200 bg-white p-6 shadow-sm'}>
              {dateRange && (
                <p className={'text-lg font-bold text-gold-200'}>
                  {dateRange}
                </p>
              )}
              {festival.location && (
                <p className={'mt-2 flex items-center gap-1.5 text-sm text-gray-600'}>
                  <MapPinIcon className={'h-4 w-4 shrink-0'} />
                  {festival.location}
                </p>
              )}
              {festival.website && (
                <a
                  href={festival.website}
                  target={'_blank'}
                  rel={'noreferrer'}
                  className={'mt-2 block text-sm font-semibold text-primary hover:underline'}
                >
                  Zur Festival-Webseite
                </a>
              )}
              <div className={'mt-6 flex flex-col gap-3'}>
                <Link
                  to={'/registrieren'}
                  className={'inline-flex items-center justify-center rounded-full bg-primary-200 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'}
                >
                  Registrieren und mitmachen
                </Link>
                <Link
                  to={'/login'}
                  className={'inline-flex items-center justify-center rounded-full border border-primary-200 px-6 py-3 text-sm font-semibold text-primary-200 hover:bg-primary-200 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'}
                >
                  Anmelden und beitreten
                </Link>
              </div>
            </aside>
          </div>
        </article>
      )}
    </PublicLayout>
  )
}
